// PRO+ purchase modal with Spotify Premium disclaimer + required checkbox.
// Also enforces: PRO+ requires PRO first — if the backend returns 403
// "pro_required", we render an inline tier picker (matching PaywallModal)
// so users can pick their Pro tier (€9,95 / €14,95 / €19,95) instead of
// being sent directly to a fixed-price €9,95 Stripe checkout. Bugfix
// 2026-07-06.
import { useEffect, useState } from "react";
import { X, Sparkles, Check, ExternalLink, AlertCircle, Music2, Lock } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { createProPlusCheckout, createCheckout, devUnlockProPlus, fetchUsage, getPricingTiers } from "../lib/client";
import { trackEvent, rememberTier } from "../lib/analytics";

export default function ProPlusModal({ open, onClose, onUnlocked }) {
  const { t } = useI18n();
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [proRequired, setProRequired] = useState(false);
  const [hasPro, setHasPro] = useState(null);
  // Tier picker state when user needs to buy PRO first.
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState("small");

  // Pre-check Pro state when the modal opens — gives instant feedback
  // (we don't make the user click "Buy" first to discover they need Pro).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const u = await fetchUsage();
        if (!cancelled) {
          setHasPro(!!u.unlocked);
          setProRequired(!u.unlocked);
        }
      } catch {
        if (!cancelled) setHasPro(null);
      }
      // Load tiers once so the pro-required state can render the picker.
      try {
        const data = await getPricingTiers();
        if (!cancelled) setTiers(data?.tiers || []);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  const handleBuyPro = async () => {
    setError(null);
    setLoading(true);
    try {
      // GA4 begin_checkout (Pro tier variant, opened from Pro+ upsell).
      const pkg = tiers.find((t) => t.id === selectedTier);
      if (pkg) {
        trackEvent("begin_checkout", {
          currency: "EUR",
          value: pkg.amount,
          items: [{ item_id: pkg.id, item_name: `Bingo ${pkg.id}`, price: pkg.amount, quantity: 1 }],
          source: "pro_plus_modal",
        });
        rememberTier({ id: pkg.id, amount: pkg.amount, currency: "EUR" });
      }
      // Pass the tier the user picked in the inline picker above.
      const data = await createCheckout(null, selectedTier);
      if (data?.url) { window.location.href = data.url; return; }
      throw new Error("No checkout URL");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    setError(null);
    setLoading(true);
    try {
      // GA4 begin_checkout for the Pro+ Spotify add-on (€4.99).
      trackEvent("begin_checkout", {
        currency: "EUR",
        value: 4.99,
        items: [{ item_id: "pro_plus", item_name: "Pro+ Spotify", price: 4.99, quantity: 1 }],
        source: "pro_plus_modal",
      });
      rememberTier({ id: "pro_plus", amount: 4.99, currency: "EUR" });
      const data = await createProPlusCheckout();
      if (data?.url) { window.location.href = data.url; return; }
      throw new Error("No checkout URL");
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Checkout failed";
      if (detail === "pro_required" || e?.response?.status === 403) {
        setProRequired(true);
      } else {
        setError(detail);
      }
      setLoading(false);
    }
  };

  const handleDev = async () => {
    setError(null);
    setLoading(true);
    try {
      await devUnlockProPlus();
      if (onUnlocked) onUnlocked();
      onClose();
    } catch (e) {
      setError(e?.message || "Dev unlock failed");
    } finally {
      setLoading(false);
    }
  };

  const showDev = typeof window !== "undefined" && window.__PRO_PLUS_DEV__ === true;

  // === PRO REQUIRED state ===
  if (proRequired) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        data-testid="pro-required-modal"
      >
        <div
          className="nb-card-lg rounded-lg bg-white w-full max-w-lg overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          <div className="relative bg-[var(--brand-yellow)] text-black p-6">
            <button
              onClick={onClose}
              className="absolute top-3 right-3 nb-border bg-white text-black p-1.5 rounded-md hover:rotate-90 transition-transform"
            >
              <X size={16} />
            </button>
            <div className="inline-flex items-center gap-2 nb-border bg-black text-white px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-3">
              <Lock size={12} /> {t("pro_required_h")}
            </div>
            <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">
              {t("pro_required_h")}
            </h2>
          </div>
          <div className="p-6">
            <p className="text-base text-neutral-800 leading-relaxed mb-5 font-medium">
              {t("pro_required_p")}
            </p>

            {/* Inline tier picker — mirrors PaywallModal so users pick
                their Pro tier before being sent to Stripe. Without this
                the flow silently defaulted to the €9,95 "small" tier. */}
            <div className="grid gap-2 mb-5" data-testid="proplus-tier-picker">
              {tiers.map((tier) => {
                const isSelected = selectedTier === tier.id;
                const label = tier.max_cards
                  ? `${t("tier_upto")} ${tier.max_cards} ${t("tier_cards")}`
                  : t("tier_unlimited");
                const amount = `€${tier.amount.toFixed(2).replace(".", ",")}`;
                const isPopular = tier.id === "medium";
                return (
                  <button
                    key={tier.id}
                    type="button"
                    onClick={() => setSelectedTier(tier.id)}
                    className={`relative nb-border rounded-md p-3 flex items-center gap-3 text-left transition-all ${
                      isSelected
                        ? "bg-white shadow-[3px_3px_0_#000] -translate-x-[1px] -translate-y-[1px]"
                        : "bg-white/60 hover:bg-white"
                    }`}
                    data-testid={`proplus-tier-option-${tier.id}`}
                  >
                    <div
                      className={`w-5 h-5 rounded-full border-2 border-black flex-shrink-0 flex items-center justify-center ${
                        isSelected ? "bg-black" : "bg-white"
                      }`}
                    >
                      {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-black text-base leading-tight">{label}</div>
                      <div className="text-[11px] text-neutral-600 font-semibold">{t("tier_per_session")}</div>
                    </div>
                    <div className="font-display font-black text-lg flex-shrink-0">{amount}</div>
                    {isPopular && (
                      <span className="absolute -top-2 right-3 text-[10px] font-black bg-[var(--brand-red)] text-white px-2 py-0.5 rounded-full uppercase tracking-widest border-2 border-black">
                        {t("tier_popular")}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleBuyPro}
                disabled={loading}
                className="flex-1 nb-button bg-[var(--brand-red)] text-white px-5 py-3 rounded-md font-bold inline-flex items-center justify-center gap-2"
                data-testid="pro-required-buy-pro"
              >
                {loading ? t("proplus_loading") : (() => {
                  const sel = tiers.find((x) => x.id === selectedTier);
                  const amt = sel ? `€${sel.amount.toFixed(2).replace(".", ",")}` : "";
                  return `${t("pro_required_buy_pro_short") || t("pro_required_buy_pro")} — ${amt}`;
                })()}
              </button>
              <button
                onClick={onClose}
                className="nb-button bg-white text-black px-5 py-3 rounded-md font-bold"
                data-testid="pro-required-cancel"
              >
                {t("pro_required_cancel")}
              </button>
            </div>
            {error && <div className="mt-3 text-sm text-red-600 font-medium">{error}</div>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="pro-plus-modal"
    >
      <div
        className="nb-card-lg rounded-lg bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-[#1DB954] to-[#0E5C2F] text-white p-6 sm:p-8">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 nb-border bg-white text-black p-1.5 rounded-md hover:rotate-90 transition-transform"
            data-testid="pro-plus-close"
          >
            <X size={16} />
          </button>
          <div className="inline-flex items-center gap-2 nb-border bg-[var(--brand-yellow)] text-black px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-3">
            <Sparkles size={12} /> {t("proplus_badge")}
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-2">
            {t("proplus_title")}
          </h2>
          <p className="text-base sm:text-lg opacity-95 font-medium">{t("proplus_subtitle")}</p>
          <div className="mt-4 inline-flex items-baseline gap-2 bg-white text-black nb-border px-4 py-2 rounded-md">
            <span className="font-display font-black text-3xl">+€4,99</span>
            <span className="text-sm font-bold opacity-70">{t("proplus_price_note")}</span>
          </div>
        </div>

        {/* Features */}
        <div className="p-6 sm:p-8">
          <h3 className="font-display font-black text-lg mb-3">{t("proplus_features_h")}</h3>
          <ul className="space-y-2.5 mb-6">
            {["proplus_feat_1", "proplus_feat_2", "proplus_feat_3", "proplus_feat_4"].map(k => (
              <li key={k} className="flex items-start gap-3 text-sm">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#1DB954] text-white flex items-center justify-center mt-0.5">
                  <Check size={12} strokeWidth={3} />
                </span>
                <span className="text-neutral-800 font-medium leading-relaxed">{t(k)}</span>
              </li>
            ))}
          </ul>

          {/* Premium disclaimer — prominent */}
          <div className="nb-border rounded-md p-4 bg-[#FFF8E1] mb-5" data-testid="premium-disclaimer">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[var(--brand-yellow)] flex items-center justify-center nb-border">
                <AlertCircle size={16} />
              </div>
              <div>
                <div className="font-display font-black text-sm mb-1">{t("proplus_premium_h")}</div>
                <p className="text-sm text-neutral-800 leading-relaxed">
                  {t("proplus_premium_p")}{" "}
                  <a
                    href="https://www.spotify.com/premium/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-bold underline inline-flex items-center gap-1"
                  >
                    Spotify Premium <ExternalLink size={11} />
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Required checkbox */}
          <label className="flex items-start gap-3 cursor-pointer mb-5 select-none p-3 nb-border rounded-md hover:bg-neutral-50" data-testid="premium-checkbox-label">
            <input
              type="checkbox"
              checked={accepted}
              onChange={e => setAccepted(e.target.checked)}
              className="mt-1 w-5 h-5 accent-[#1DB954]"
              data-testid="premium-checkbox"
            />
            <span className="text-sm text-neutral-800 font-medium leading-relaxed">
              {t("proplus_checkbox_label")}
            </span>
          </label>

          {error && (
            <div className="mb-4 p-3 nb-border rounded-md bg-red-50 text-red-700 text-sm font-medium" data-testid="proplus-error">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handlePurchase}
              disabled={!accepted || loading}
              className={`flex-1 nb-button px-5 py-3 rounded-md font-bold inline-flex items-center justify-center gap-2 ${
                accepted && !loading
                  ? "bg-[#1DB954] text-white hover:-translate-x-0.5 hover:-translate-y-0.5"
                  : "bg-neutral-300 text-neutral-500 cursor-not-allowed"
              }`}
              data-testid="proplus-buy-btn"
            >
              <Music2 size={18} /> {loading ? t("proplus_loading") : t("proplus_buy_cta")}
            </button>
            <button
              onClick={onClose}
              className="nb-button bg-white text-black px-5 py-3 rounded-md font-bold"
              data-testid="proplus-later-btn"
            >
              {t("proplus_later")}
            </button>
          </div>

          {showDev && (
            <div className="mt-4 pt-4 border-t border-dashed border-neutral-300">
              <button
                onClick={handleDev}
                className="text-xs font-bold text-neutral-500 hover:text-black inline-flex items-center gap-1"
                data-testid="proplus-dev-unlock"
              >
                🛠️ Dev: unlock Pro+ zonder betalen
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
